/**
 * Catalogue des permissions.
 *
 * La source de vérité est `GET /{company}/permissions` : le serveur renvoie les
 * groupes déjà traduits dans la langue de l'appelant, avec un drapeau
 * `grantable` par permission. Ce fichier n'apporte que ce que l'API n'expose
 * pas — l'icône et l'ordre d'affichage des groupes — plus un catalogue de repli
 * utilisé quand l'appel échoue (les libellés retombent alors sur i18n).
 */

export const OTHER_GROUP_KEY = 'other'
export const OTHER_GROUP_ICON = 'fal fa-cog'

export const PERMISSION_GROUP_ORDER = [
    'posts',
    'comments',
    'messages',
    'forms',
    'form-types',
    'response-templates',
    'news',
    'news-categories',
    'events',
    'users',
    'citizens',
    'roles',
    'company'
]

export const PERMISSION_GROUP_ICONS = {
    posts: 'fal fa-ticket',
    comments: 'fal fa-comments',
    messages: 'fal fa-envelope',
    forms: 'fal fa-poll',
    'form-types': 'fal fa-layer-group',
    'response-templates': 'fal fa-reply',
    news: 'fal fa-newspaper',
    'news-categories': 'fal fa-tags',
    events: 'fal fa-calendar-star',
    users: 'fal fa-users',
    citizens: 'fal fa-user-friends',
    roles: 'fal fa-user-shield',
    company: 'fal fa-building'
}

/**
 * Repli hors ligne — miroir des 47 permissions / 13 groupes de l'API.
 * Attention : `roles.manage`, `company.settings`, `company.statistics` et
 * `company.statistics.configure` ne suivent pas le motif `groupe.verbe`.
 */
export const FALLBACK_PERMISSION_GROUPS = [
    {
        group: 'posts',
        permissions: [
            'posts.view',
            'posts.create',
            'posts.update',
            'posts.delete',
            'posts.assign',
            'posts.export'
        ]
    },
    { group: 'comments', permissions: ['comments.view', 'comments.create', 'comments.delete'] },
    { group: 'messages', permissions: ['messages.view', 'messages.create'] },
    { group: 'forms', permissions: ['forms.view', 'forms.create', 'forms.update', 'forms.delete'] },
    {
        group: 'form-types',
        permissions: [
            'form-types.view',
            'form-types.create',
            'form-types.update',
            'form-types.delete'
        ]
    },
    {
        group: 'response-templates',
        permissions: [
            'response-templates.view',
            'response-templates.create',
            'response-templates.update',
            'response-templates.delete'
        ]
    },
    { group: 'news', permissions: ['news.view', 'news.create', 'news.update', 'news.delete'] },
    {
        group: 'news-categories',
        permissions: [
            'news-categories.view',
            'news-categories.create',
            'news-categories.update',
            'news-categories.delete'
        ]
    },
    { group: 'events', permissions: ['events.view', 'events.create', 'events.update', 'events.delete'] },
    {
        group: 'users',
        permissions: [
            'users.view',
            'users.create',
            'users.update',
            'users.delete',
            'users.impersonate'
        ]
    },
    { group: 'citizens', permissions: ['citizens.view', 'citizens.create', 'citizens.update'] },
    { group: 'roles', permissions: ['roles.manage'] },
    {
        group: 'company',
        permissions: ['company.settings', 'company.statistics', 'company.statistics.configure']
    }
]

/**
 * Dépendances entre permissions.
 *
 * Certains écrans chargent des données de référence gardées par une autre
 * permission que celle de leur route : l'éditeur de formulaire
 * (`forms.create` / `forms.update`) appelle `GET /form-types` et
 * `GET /users/find` dès son `created()`. Sans le droit correspondant, le 403
 * est intercepté globalement et éjecte l'utilisateur vers `/403` — un écran
 * auquel il a pourtant droit devient inaccessible.
 *
 * Cette carte est un dépannage : la place naturelle de l'information est le
 * catalogue serveur. Dès que `GET /permissions` exposera un champ `requires`
 * par permission, il prendra le pas (voir `permissionRequirements`) et cette
 * constante pourra disparaître.
 */
export const PERMISSION_REQUIREMENTS = {
    'forms.create': ['form-types.view', 'users.view'],
    'forms.update': ['form-types.view', 'users.view']
}

/** Dépendances directes d'une permission, catalogue serveur prioritaire. */
export function permissionRequirements(permission) {
    if (!permission) return []
    if (Array.isArray(permission.requires)) return permission.requires
    const name = typeof permission === 'string' ? permission : permission.name
    return PERMISSION_REQUIREMENTS[name] || []
}

/**
 * Ferme transitivement l'ensemble : accorder A doit accorder ce dont A dépend,
 * y compris en cascade. Le `seen` protège d'un cycle déclaré par erreur.
 */
export function resolvePermissionRequirements(names, catalogue = null) {
    const byName = catalogue || {}
    const out = new Set()
    const walk = (name) => {
        permissionRequirements(byName[name] || name).forEach((required) => {
            if (out.has(required)) return
            out.add(required)
            walk(required)
        })
    }
    ;(Array.isArray(names) ? names : [names]).forEach(walk)
    return Array.from(out)
}

function groupIcon(key) {
    return PERMISSION_GROUP_ICONS[key] || OTHER_GROUP_ICON
}

function sortGroups(groups) {
    return groups.slice().sort((a, b) => {
        const rankA = PERMISSION_GROUP_ORDER.indexOf(a.key)
        const rankB = PERMISSION_GROUP_ORDER.indexOf(b.key)
        return (
            (rankA === -1 ? PERMISSION_GROUP_ORDER.length : rankA) -
            (rankB === -1 ? PERMISSION_GROUP_ORDER.length : rankB)
        )
    })
}

/**
 * Met la réponse de `GET /permissions` au format consommé par l'éditeur :
 * `[{ key, label, icon, permissions: [{ name, label, grantable }] }]`.
 */
export function normalizePermissionGroups(apiGroups) {
    if (!Array.isArray(apiGroups)) return []
    const groups = apiGroups
        .filter((group) => group && group.group)
        .map((group) => ({
            key: group.group,
            label: group.label || '',
            icon: groupIcon(group.group),
            permissions: (group.permissions || [])
                .filter((permission) => permission && permission.name)
                .map((permission) => ({
                    name: permission.name,
                    label: permission.label || '',
                    grantable: permission.grantable !== false,
                    requires: Array.isArray(permission.requires)
                        ? permission.requires
                        : PERMISSION_REQUIREMENTS[permission.name] || []
                }))
        }))
        .filter((group) => group.permissions.length)
    return sortGroups(groups)
}

export function fallbackPermissionGroups() {
    return FALLBACK_PERMISSION_GROUPS.map((group) => ({
        key: group.group,
        label: '',
        icon: groupIcon(group.group),
        permissions: group.permissions.map((name) => ({
            name,
            label: '',
            grantable: true,
            requires: PERMISSION_REQUIREMENTS[name] || []
        }))
    }))
}

/**
 * Rattache les permissions accordées au rôle mais absentes du catalogue
 * (permission dépréciée côté serveur, catalogue plus à jour) : sans ça, un
 * droit réellement actif deviendrait invisible — et impossible à retirer.
 */
export function withGrantedPermissions(groups, granted = []) {
    const known = new Set()
    groups.forEach((group) => {
        group.permissions.forEach((permission) => known.add(permission.name))
    })

    const orphans = granted.filter((name) => name && !known.has(name))
    if (!orphans.length) return groups

    const merged = groups.map((group) => ({
        ...group,
        permissions: [...group.permissions]
    }))

    orphans.forEach((name) => {
        const key = String(name).split('.')[0]
        let group = merged.find((candidate) => candidate.key === key)
        if (!group) {
            group = merged.find((candidate) => candidate.key === OTHER_GROUP_KEY)
        }
        if (!group) {
            group = {
                key: OTHER_GROUP_KEY,
                label: '',
                icon: OTHER_GROUP_ICON,
                permissions: []
            }
            merged.push(group)
        }
        group.permissions.push({
            name,
            label: '',
            grantable: true,
            requires: PERMISSION_REQUIREMENTS[name] || []
        })
    })

    return sortGroups(merged)
}
